import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Spinner as ReactSpinner } from 'react-bootstrap';
import { shallowEqual, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ScreeningInfo from './ScreeningInfo';
import { AppState } from '../../store/store';
import { ApiSanctionAlert } from '../../api';
import { PersonLink } from '../../common/PersonLink';
import { Link } from 'react-router-dom';
import { useQueryAttributeMappings } from '../../schema/mappings/mappingsActions';
import './ScreeningAlert.scss';
import { sanctionAlertsApi } from '../../apis';
import { getUserId } from '../../auth/authReducer';
import { screeningDisplayNames } from '../configure/ScreeningDataSetup';
import { useAlertStatuses } from '../alert-status/alertStatusesHelper';
import { useAlertFlow } from '../../alerts/flow/useAlertFlow';
import { InternalApiAlertType } from '../../api-internal';
import SalvPage from '../../common/SalvPage';
import { getScreeningFlowDisplayId } from '../configure/new/helper';
import DataLoadingErrorFallback from '../../common/errors/DataLoadingErrorFallback';
import { SalvProgressCircle } from '@salvapp/ui-components';

export const ScreeningAlert = () => {
  const { userId, updateRequestedAt } = useSelector(
    (state: AppState) => ({
      userId: getUserId(state),
      updateRequestedAt: state.screeningAlerts.lastAlertUpdate,
    }),
    shallowEqual
  );

  const alertId = Number(useParams<{ id: string }>().id);
  const [alert, setAlert] = useState<ApiSanctionAlert | null>(null);
  const [fetching, setFetching] = useState(true);

  const alertAssignedToSelf = useMemo(() => {
    return alert?.assignments?.some((assignment) => !assignment.endTime && assignment.assignee.id === userId) ?? false;
  }, [alert]);
  const [setDirty, resetBlocker, renderNavigationButtons] = useAlertFlow(
    alertAssignedToSelf,
    alertId,
    InternalApiAlertType.SCREENING
  );

  const fetchAlert = useCallback(() => {
    setFetching(true);
    sanctionAlertsApi
      .getSanctionAlert({ sanctionAlertId: alertId })
      .then((alert) => {
        setAlert(alert);
      })
      .catch(() => null)
      .finally(() => {
        setFetching(false);
      });
  }, [alertId]);

  useEffect(() => {
    resetBlocker();
    setAlert(null);
    fetchAlert();
  }, [alertId]);

  useEffect(() => {
    fetchAlert();
  }, [updateRequestedAt]);

  // ignore -> fetches statuses for all underlying pages in case they are missing,
  // should be resolved by using this hook in correct places.
  useAlertStatuses();

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const LinksToEntities = ({ alert }: { alert: ApiSanctionAlert }) => {
    const { person, transaction } = alert.entity;
    return (
      <>
        {person && (
          <div>
            <span className='text-secondary m-r-4'>Customer:</span>
            <PersonLink person={person} mappings={attributeMappings.map} />
          </div>
        )}
        {transaction && (
          <div>
            <span className='text-secondary m-r-4'>Transaction:</span>
            <Link to={`/transactions/${transaction.id}`}>{transaction.id}</Link>
          </div>
        )}
      </>
    );
  };

  return (
    <SalvPage
      title={alert ? `${screeningDisplayNames[alert.type]} alert ${alertId}` : ''}
      actions={renderNavigationButtons()}
      subTitle={
        alert ? (
          <div style={{ display: 'flex', gap: '24px' }}>
            <LinksToEntities alert={alert} />
            <div>
              <span className='text-secondary m-r-4'>Flow ID:</span>
              <span className={'text-secondary'}>{getScreeningFlowDisplayId(alert.screeningFlowId)}</span>
            </div>
          </div>
        ) : undefined
      }
    >
      {fetching ? (
        <Card>
          <Card.Body>
            <div className='small'>Loading alert information...</div>
            <ReactSpinner className='loader-spinner' animation='border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </ReactSpinner>
          </Card.Body>
        </Card>
      ) : alert !== null && alert.id === alertId ? (
        <ScreeningInfo onChange={setDirty} sanctionAlertId={alertId} alert={alert} />
      ) : (
        <Card>
          <Card.Body>
            <div className='small'>Could not find alert with ID: {alertId}.</div>
          </Card.Body>
        </Card>
      )}
    </SalvPage>
  );
};
export default ScreeningAlert;
